/* Created by Interactive 63 - https://interactive63.com */
/**
 * The preview: the real HTML export running in a sandboxed iframe. The sandbox allows scripts
 * but not `allow-same-origin`, so exported code runs in an opaque origin and cannot reach the
 * editor's storage or DOM. Used by the Preview dialog and the Export dialog's Preview tab.
 */
import { compileScene } from '../../core/compiler/compile';
import { htmlDocument, type PreviewBackground } from '../../core/compiler/documents';
import { RUNTIME_CODE } from '../runtime-source';
import type { ExportSettings } from '../../core/model/types';
import type { Store } from '../store/store';
import { h } from '../ui/dom';
import {
  PREVIEW_DOCUMENT,
  PREVIEW_READY,
  type PreviewDocumentMessage,
} from '../../preview-protocol';

type RepeatChoice = 'exported' | '3' | 'infinite';
type WidthChoice = '320' | '768' | 'full';

export class PreviewFrame {
  readonly el: HTMLElement;
  private readonly frame: HTMLIFrameElement;
  private background: PreviewBackground = 'white';
  private reduced = false;
  private javascript = true;
  private repeat: RepeatChoice = 'exported';
  private readonly console: HTMLElement;
  private pending: string | null = null;

  /** Builds the controls and the iframe. Call `refresh` to render the current project. */
  constructor(private readonly store: Store) {
    // Never add allow-same-origin: it would let exported code reach the editor.
    this.frame = h('iframe', {
      class: 'preview-iframe',
      sandbox: 'allow-scripts',
      title: 'Animation preview',
    });
    // The host page asks for its document once it has loaded; the sandboxed frame has an opaque
    // origin, so the sender is recognised by window identity.
    window.addEventListener('message', (event) => {
      const data = event.data as { type?: unknown } | null;
      if (event.source !== this.frame.contentWindow || data?.type !== PREVIEW_READY) return;
      if (this.pending === null) return;
      const message: PreviewDocumentMessage = { type: PREVIEW_DOCUMENT, html: this.pending };
      this.pending = null;
      this.frame.contentWindow?.postMessage(message, '*');
    });
    this.console = this.apiConsole();
    this.el = h(
      'div',
      { class: 'preview-frame' },
      this.controls(),
      h(
        'p',
        { class: 'preview-note' },
        'The background is only a backdrop for this preview. The export is transparent unless the artwork has a shape filling the viewBox.',
      ),
      this.console,
      h('div', { class: 'preview-stage' }, this.frame),
    );
  }

  /** Rebuilds the document from the current project, which also replays the animation. */
  refresh(): void {
    const { project } = this.store.getState();
    // The preview page has no neighbouring files, so the runtime is always inlined. JavaScript is
    // on by default because shape morphs only play in every browser through the runtime.
    const settings: ExportSettings = {
      ...project.export,
      codeType: this.javascript ? 'css-js' : 'css',
      runtimeDelivery: 'inline',
    };
    const scene = compileScene(project, {
      target: 'export',
      exportSettings: settings,
      forceReducedMotion: this.reduced,
      iterations: this.repeat === 'exported' ? undefined : this.repeat === '3' ? 3 : 'infinite',
    });
    this.pending = htmlDocument(scene, settings, project.name, {
      background: this.background,
      runtimeCode: RUNTIME_CODE,
      preview: true,
    });
    // Navigating again, even to the same address, gives the host page a fresh start each time.
    this.frame.src = `${import.meta.env.BASE_URL}preview.html`;
    this.console.hidden = !this.javascript;
  }

  /** Sends a command to the scene inside the preview. */
  private send(message: { t: string; p?: number }): void {
    this.frame.contentWindow?.postMessage(message, '*');
  }

  /** The API console: buttons and a seek slider that drive the scene in the preview. */
  private apiConsole(): HTMLElement {
    const button = (label: string, t: string): HTMLButtonElement => {
      const b = h('button', { type: 'button' }, label);
      b.addEventListener('click', () => {
        this.send({ t });
      });
      return b;
    };
    const seek = h('input', {
      type: 'range',
      min: 0,
      max: 1,
      step: 0.01,
      value: 0,
      'aria-label': 'Seek',
    });
    seek.addEventListener('input', () => {
      this.send({ t: 'seek', p: Number(seek.value) });
    });
    return h(
      'details',
      { class: 'api-console' },
      h('summary', null, 'API console'),
      h(
        'div',
        { class: 'api-console-body' },
        button('Play', 'play'),
        button('Pause', 'pause'),
        button('Reverse', 'reverse'),
        button('Restart', 'restart'),
        h('label', { class: 'preview-control' }, h('span', null, 'Seek'), seek),
      ),
    );
  }

  /** Builds the control row. */
  private controls(): HTMLElement {
    const replay = h('button', { type: 'button' }, 'Replay');
    replay.addEventListener('click', () => {
      this.refresh();
    });

    /** A select that updates one setting and rebuilds the preview. */
    const choice = <T extends string>(
      label: string,
      options: [T, string][],
      initial: T,
      set: (value: T) => void,
    ): HTMLElement => {
      const select = h(
        'select',
        { 'aria-label': label },
        ...options.map(([value, text]) =>
          h('option', { value, selected: value === initial }, text),
        ),
      );
      select.addEventListener('change', () => {
        set(select.value as T);
        this.refresh();
      });
      return h('label', { class: 'preview-control' }, h('span', null, label), select);
    };

    const reduced = h('input', { type: 'checkbox' });
    reduced.addEventListener('change', () => {
      this.reduced = reduced.checked;
      this.refresh();
    });

    const javascript = h('input', { type: 'checkbox', checked: this.javascript });
    javascript.addEventListener('change', () => {
      this.javascript = javascript.checked;
      this.refresh();
    });

    return h(
      'div',
      { class: 'preview-controls' },
      replay,
      h(
        'label',
        {
          class: 'preview-control',
          title:
            'Runs the preview with the JavaScript runtime. Turn it off to see the CSS-only result.',
        },
        javascript,
        ' JavaScript',
      ),
      choice<PreviewBackground>(
        'Background',
        [
          ['white', 'White'],
          ['dark', 'Dark'],
          ['checker', 'Checkerboard'],
        ],
        this.background,
        (value) => {
          this.background = value;
        },
      ),
      choice<WidthChoice>(
        'Width',
        [
          ['full', 'Full'],
          ['768', '768 px'],
          ['320', '320 px'],
        ],
        'full',
        (value) => {
          this.frame.style.width = value === 'full' ? '100%' : `${value}px`;
        },
      ),
      choice<RepeatChoice>(
        'Scene repeats',
        [
          ['exported', 'As exported'],
          ['3', '3 times'],
          ['infinite', 'Infinite'],
        ],
        this.repeat,
        (value) => {
          this.repeat = value;
        },
      ),
      h('label', { class: 'preview-control' }, reduced, ' Simulate reduced motion'),
    );
  }
}
